<template>
  <div class="message-read-by">
    <FriedParagraph :size="ParagraphSize.Small" class="label">{{
      t('message_seen_by_short')
    }}</FriedParagraph>
    <div class="read-by-container">
      <FriedAvatarGroup
        :avatars="avatars"
        :size="AvatarSize.Small"
        :visible-avatars="4"
        clickable
        :margin="4"
        class="participant-avatar"
        @click="toggleModal(true)"
      />
      <FriedModal v-if="showModal" @close="toggleModal(false)">
        <template #header>
          <FriedModalHeader :header="t('message_seen_by')" />
        </template>
        <FriedList>
          <FriedListItem
            v-for="avatar of avatars"
            :key="avatar.id"
            :title="avatar.name"
            :description="avatar.timestamp"
          >
            >
            <template #left>
              <div>
                <FriedAvatar :name="avatar.name" :thumb-url="avatar.thumbUrl" />
              </div>
            </template>
          </FriedListItem>
        </FriedList>
      </FriedModal>
    </div>
  </div>
</template>

<script lang="ts">
import { AvatarSize, ParagraphSize } from '@getaccept/fried-tofu';
import type { ComputedRef, PropType, Ref } from 'vue';
import { computed, toRefs, ref, defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { ChatHelper } from '../helpers/chat.helper';
import type { ChatParticipant, LastReadBy } from '../types';

export default defineComponent({
  name: 'MessageReadBy',
  props: {
    reads: {
      type: Array as PropType<Array<LastReadBy>>,
      default: () => [],
    },
  },
  setup(props) {
    const { t } = useI18n();
    const { reads } = toRefs(props);
    const getName = (chatParticipant: ChatParticipant): string =>
      chatParticipant.participant?.fullName ||
      chatParticipant.participant?.email ||
      chatParticipant.user?.fullName ||
      chatParticipant.user?.email;

    const getThumbUrl = (chatParticipant: ChatParticipant): string =>
      chatParticipant.participant?.thumbUrl || chatParticipant.user?.thumbUrl;

    const avatars: ComputedRef<
      {
        name: string;
        thumbUrl: string;
        timestamp: string;
        id: string;
      }[]
    > = computed(() =>
      reads.value.map(({ readBy }: LastReadBy) => ({
        id: readBy.participant.participantId || readBy.participant.userId,
        name: getName(readBy.participant),
        thumbUrl: getThumbUrl(readBy.participant),
        timestamp: ChatHelper.fullTimestamp(readBy.timestamp),
      }))
    );

    const showModal: Ref<boolean> = ref(false);

    const toggleModal = (isOpen: boolean): void => {
      showModal.value = isOpen;
    };

    return {
      AvatarSize,
      avatars,
      showModal,
      toggleModal,
      getName,
      getThumbUrl,
      ParagraphSize,
      t,
    };
  },
});
</script>

<style lang="scss" scoped>
.message-read-by {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: var(--spacing-25) var(--spacing-100);

  > .label {
    color: var(--text-gray);
  }

  .read-by-container {
    display: flex;
    flex-direction: row;

    .base-avatar-counter-container.clickable {
      transition: all 0.1s linear;

      &:hover {
        transform: scale(1.1);
        cursor: pointer;
      }
    }
  }
}
</style>
<style lang="scss">
.read-by-container {
  margin-top: var(--spacing-25);
}

.read-by-details {
  background-color: var(--white);
  padding: var(--spacing-50);
  border-radius: var(--radius-medium);
}
</style>
